import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../../../lib/api';
import { toast } from 'sonner';
import { Briefcase, Plus, X, Users, MapPin, CheckCircle2, Loader2, Zap, Pencil } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600 border-slate-300',
  open: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  filled: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-red-50 text-red-700 border-red-200',
};

const DEPARTMENTS = ['Projects/Site', 'Procurement', 'Engineering', 'HR & Admin', 'Finance & Accounts', 'Business Development', 'Quality', 'Safety/HSE', 'Stores', 'IT', 'Management'];

const blank = { title: '', department: '', grade: '', headcount: 1, jd_summary: '', location: '', closing_date: '', max_ctc: '', min_experience_years: '', status: 'open', source: 'external' };

export default function JobOpenings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [autoFetchingId, setAutoFetchingId] = useState(null);
  const [fetchResults, setFetchResults] = useState({});

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const canWrite = ['super_admin', 'admin', 'hr', 'md'].includes(role) || dept === 'HR' || dept === 'MD';

  const [editingJob, setEditingJob] = useState(null);
  const [editForm, setEditForm] = useState({});

  const openEdit = (job) => {
    setEditForm({
      title: job.title || '',
      department: job.department || '',
      grade: job.grade || '',
      headcount: job.headcount || 1,
      jd_summary: job.jd_summary || '',
      required_skills: (job.required_skills || []).join(', '),
      min_experience_years: job.min_experience_years || '',
      max_ctc: job.max_ctc || '',
      location: job.location || '',
      closing_date: job.closing_date || '',
      status: job.status || 'open',
      source: job.source || 'external',
    });
    setEditingJob(job);
  };

  const saveEdit = async () => {
    if (!editForm.title?.trim()) { toast.error('Job title is required'); return; }
    try {
      const payload = {
        ...editForm,
        headcount: Number(editForm.headcount) || 1,
        min_experience_years: editForm.min_experience_years ? Number(editForm.min_experience_years) : null,
        max_ctc: editForm.max_ctc ? Number(editForm.max_ctc) : null,
        required_skills: editForm.required_skills
          ? editForm.required_skills.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      await apiPut(`/hr/jobs/${editingJob.id}`, payload);
      toast.success('Job opening updated');
      setEditingJob(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const load = () => apiGet('/hr/jobs', filter ? { status: filter } : undefined)
    .then((r) => setJobs(r.items || [])).catch(() => toast.error('Failed to load jobs'));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

const handleAutoFetch = async (openingId) => {
    setAutoFetchingId(openingId);
    try {
      const r = await apiPost(`/hr/recruitment/openings/${openingId}/auto-fetch?min_score=30`);
      setFetchResults(prev => ({ ...prev, [openingId]: r }));
      if (r.added > 0) {
        toast.success(`${r.added} candidate(s) auto-added to Applied stage`);
      } else {
        toast.info(
          `No matching candidates found (checked ${r.total_pool || 0} in pool). ` +
          `Add candidates with matching skills to the Candidate Pool first.`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-fetch failed');
    } finally { setAutoFetchingId(null); }
  };

  const submit = async (e) => {    e.preventDefault();
    if (!form.title.trim()) { toast.error('Job title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form,
        headcount: Number(form.headcount) || 1,
        max_ctc: form.max_ctc ? Number(form.max_ctc) : null,
        min_experience_years: form.min_experience_years ? Number(form.min_experience_years) : null,
      };
      const created = await apiPost('/hr/jobs', payload);
      toast.success('Job opening created');
      setShowForm(false); setForm(blank);
      nav(`/app/hr/recruitment/jobs/${created.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="job-openings-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Recruitment</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Job Openings</h1>
          <p className="text-sm text-slate-600 mt-1">Create roles and track candidates through the hiring pipeline.</p>
        </div>
        {canWrite && (
          <button onClick={() => { setForm(blank); setShowForm(true); }} data-testid="new-job-btn"
            className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2">
            <Plus size={14} /> New Opening
          </button>
        )}
      </div>

      <div className="flex gap-2">
        {['', 'open', 'draft', 'filled', 'closed'].map((s) => (
          <button key={s || 'all'} onClick={() => setFilter(s)} data-testid={`filter-${s || 'all'}`}
            className={`px-3 py-1.5 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider border ${filter === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-200'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="jobs-empty">
          <Briefcase size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No job openings yet.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobs-grid">
          {jobs.map((j) => (
            <div key={j.id} className="relative text-left bg-white border border-slate-200 rounded-sm p-4 hover:shadow-md transition-shadow" data-testid={`job-card-${j.id}`}>
              <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={() => nav(`/app/hr/recruitment/jobs/${j.id}`)}>
                <div className="font-display font-bold text-sm">{j.title}</div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-[9px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_COLORS[j.status] || STATUS_COLORS.draft}`}>{j.status}</span>
                  {canWrite && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(j); }}
                      className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-[#3b4872]"
                      title="Edit opening"
                      data-testid={`job-edit-${j.id}`}
                    >
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-1 cursor-pointer" onClick={() => nav(`/app/hr/recruitment/jobs/${j.id}`)}>{j.department || '—'}{j.grade ? ` · ${j.grade}` : ''}</div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-600 cursor-pointer" onClick={() => nav(`/app/hr/recruitment/jobs/${j.id}`)}>
                <span className="flex items-center gap-1"><Users size={11} /> {j.candidate_count || 0}</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={11} /> {j.headcount} open</span>
                {j.location && <span className="flex items-center gap-1"><MapPin size={11} /> {j.location}</span>}
              </div>
              {j.closing_date && <div className="text-[10px] text-amber-600 mt-2 cursor-pointer" onClick={() => nav(`/app/hr/recruitment/jobs/${j.id}`)}>Closes {j.closing_date}</div>}
              {canWrite && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAutoFetch(j.id); }}
                  disabled={autoFetchingId === j.id}
                  className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#3b4872]/40 text-[#3b4872] hover:bg-indigo-50 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-40"
                  data-testid={`auto-fetch-${j.id}`}
                >
                  {autoFetchingId === j.id
                    ? <><Loader2 size={11} className="animate-spin" /> Fetching…</>
                    : <><Zap size={11} /> Auto Fetch Candidates</>
                  }
                </button>
              )}
              {fetchResults[j.id] && fetchResults[j.id].added > 0 && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-sm text-[10px] text-blue-800">
                  <strong>{fetchResults[j.id].added} candidate(s) added to Applied</strong>
                  {fetchResults[j.id].candidates.slice(0, 3).map((c) => (
                    <div key={c.candidate_id} className="mt-0.5">{c.candidate_name} — {c.match_score}% match</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editingJob && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-md w-full max-w-xl my-8 shadow-xl" data-testid="job-edit-modal">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="font-display font-bold text-lg">Edit Job Opening</h2>
              <button onClick={() => setEditingJob(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Title *</label>
                <input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1" data-testid="job-edit-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Department</label>
                  <select value={editForm.department} onChange={e => setEditForm({...editForm, department: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1 bg-white">
                    <option value="">Select…</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Grade</label>
                  <input value={editForm.grade} onChange={e => setEditForm({...editForm, grade: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Headcount</label>
                  <input type="number" min="1" value={editForm.headcount} onChange={e => setEditForm({...editForm, headcount: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Min Experience (yrs)</label>
                  <input type="number" min="0" value={editForm.min_experience_years} onChange={e => setEditForm({...editForm, min_experience_years: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Location</label>
                  <input value={editForm.location} onChange={e => setEditForm({...editForm, location: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Closing Date</label>
                  <input type="date" value={editForm.closing_date} onChange={e => setEditForm({...editForm, closing_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Required Skills (comma-separated)</label>
                <input value={editForm.required_skills} onChange={e => setEditForm({...editForm, required_skills: e.target.value})}
                  placeholder="AutoCAD, Primavera, MS Project"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1" />
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">JD Summary</label>
                <textarea rows={3} value={editForm.jd_summary} onChange={e => setEditForm({...editForm, jd_summary: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1" />
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Status</label>
                <select value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mt-1 bg-white">
                  <option value="draft">Draft</option>
                  <option value="open">Open</option>
                  <option value="filled">Filled</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button onClick={() => setEditingJob(null)} className="px-4 py-2 border border-slate-300 rounded-sm text-sm font-display hover:bg-slate-50">Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-sm font-display font-bold hover:bg-[#2d3856]" data-testid="job-edit-save">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowForm(false)} />
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col" data-testid="job-form-drawer">
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#3b4872] text-white">
              <h2 className="font-display font-bold tracking-wide uppercase text-sm">New Job Opening</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </header>
            <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-3">
              <Field label="Job Title *"><input data-testid="job-title-input" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <Field label="Department">
                <select data-testid="job-dept-select" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                  <option value="">Select…</option>
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Grade"><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} /></Field>
                <Field label="Headcount"><input type="number" min="1" data-testid="job-headcount-input" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Min Experience (yrs)"><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.min_experience_years} onChange={(e) => setForm({ ...form, min_experience_years: e.target.value })} /></Field>
                <Field label="Max CTC (₹/yr)"><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.max_ctc} onChange={(e) => setForm({ ...form, max_ctc: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Location"><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
                <Field label="Closing Date"><input type="date" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.closing_date} onChange={(e) => setForm({ ...form, closing_date: e.target.value })} /></Field>
              </div>
              <Field label="JD Summary"><textarea rows={4} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.jd_summary} onChange={(e) => setForm({ ...form, jd_summary: e.target.value })} /></Field>
              <Field label="Status">
                <select className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="open">Open</option><option value="draft">Draft</option>
                </select>
              </Field>
              <button type="submit" disabled={saving} data-testid="job-submit-btn"
                className="w-full py-2.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] disabled:opacity-50">
                {saving ? 'Creating…' : 'Create Opening'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
